import { useState, useEffect, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ToggleLeft, ToggleRight, Music, ChevronLeft, Zap, Settings, FileText, LogOut, Upload, Trash2, Plus, Tag, Image as ImageIcon, Pencil, RefreshCw, BarChart3, Users, Headphones, Heart, Clock, Sparkles, Flame } from 'lucide-react';
import VoiceWave from '@/components/VoiceWave';
import WaveLoader from '@/components/WaveLoader';
import ChallengeAdminPanel from '@/components/ChallengeAdminPanel';
import { toast } from 'sonner';
import AdminLoginPage from './AdminLoginPage';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getToken = () => localStorage.getItem('admin_token');

const authFetch = (url, opts = {}) => {
  const token = getToken();
  return fetch(url, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...(opts.headers || {}) },
  });
};


function TrackTitle({ trackId, title, onRename }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(title);
  const inputRef = useRef(null);

  const save = () => {
    if (value.trim() && value.trim() !== title) onRename(trackId, value.trim());
    setEditing(false);
  };

  useEffect(() => { if (editing) inputRef.current?.focus(); }, [editing]);

  if (editing) {
    return (
      <div className="flex-1 min-w-0">
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={save}
          onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') { setValue(title); setEditing(false); } }}
          className="w-full bg-zinc-700 border border-amber-500/50 rounded px-2 py-0.5 text-xs text-white focus:outline-none"
          data-testid={`rename-input-${trackId}`}
        />
      </div>
    );
  }

  return (
    <div className="flex-1 min-w-0 flex items-center gap-1">
      <span className="text-xs text-zinc-300 truncate">{title}</span>
      <button
        onClick={() => setEditing(true)}
        className="opacity-0 group-hover:opacity-100 text-zinc-600 hover:text-amber-400 transition-all p-0.5 flex-shrink-0"
        title="Rename"
      >
        <Pencil className="w-3 h-3" />
      </button>
    </div>
  );
}

function CategoryUploader({ slug, categoryName, onUploaded }) {
  const fileRef = useRef(null);
  const imageRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [title, setTitle] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  const processFile = (file) => {
    if (!file) return;
    if (!file.name.match(/\.(mp3|mov|mp4|wav|m4a)$/i) && !file.type.startsWith('audio/') && !file.type.startsWith('video/')) {
      toast.error('Please select an MP3 or MOV file');
      return;
    }
    setSelectedFile(file);
    setTitle(file.name.replace(/\.[^.]+$/, '').replace(/_/g, ' '));
    setShowForm(true);
  };

  const handleFileSelect = (e) => processFile(e.target.files?.[0]);

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    processFile(e.dataTransfer.files?.[0]);
  };

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSelectedImage(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('category', slug);
      formData.append('title', title);
      if (selectedImage) formData.append('image', selectedImage);

      const res = await fetch(`${API_URL}/api/admin/tracks`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` },
        body: formData,
      });
      if (!res.ok) throw new Error('Upload failed');
      const data = await res.json();
      toast.success(`Uploaded: ${data.title}`);
      cancel();
      onUploaded();
    } catch {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const cancel = () => {
    setShowForm(false);
    setSelectedFile(null);
    setSelectedImage(null);
    setImagePreview(null);
    setTitle('');
    if (fileRef.current) fileRef.current.value = '';
    if (imageRef.current) imageRef.current.value = '';
  };

  return (
    <div className="mt-3 pt-3 border-t border-white/5">
      <input ref={fileRef} type="file" accept=".mp3,.mov,.mp4,.wav,.m4a,audio/*,video/*" onChange={handleFileSelect} className="hidden" />
      <input ref={imageRef} type="file" accept="image/*" onChange={handleImageSelect} className="hidden" />

      {!showForm ? (
        <div
          onClick={() => fileRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragEnter={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={(e) => { e.preventDefault(); setDragOver(false); }}
          onDrop={handleDrop}
          className={`border border-dashed rounded-lg p-4 text-center cursor-pointer transition-all ${
            dragOver ? 'border-amber-500 bg-amber-500/5' : 'border-zinc-800 hover:border-amber-500/40'
          }`}
          data-testid={`upload-btn-${slug}`}
        >
          <Upload className={`w-5 h-5 mx-auto mb-1 transition-colors ${dragOver ? 'text-amber-400' : 'text-zinc-700'}`} />
          <p className="text-xs text-zinc-500">Drop MP3/MOV here or <span className="text-amber-400">browse</span></p>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-[11px] text-zinc-500">File: <span className="text-zinc-300">{selectedFile?.name}</span></p>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Track title"
            className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white focus:border-amber-500 focus:outline-none"
            data-testid={`upload-title-${slug}`}
          />
          {/* Image picker */}
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => imageRef.current?.click()}
              className="flex items-center gap-1.5 bg-zinc-800 border border-zinc-700 hover:border-zinc-600 px-3 py-1.5 rounded-lg text-xs text-zinc-400 transition-colors"
              data-testid={`upload-image-${slug}`}
            >
              <ImageIcon className="w-3 h-3" />
              {selectedImage ? 'Change image' : 'Add cover image'}
            </button>
            {imagePreview && (
              <img src={imagePreview} alt="Preview" className="w-8 h-8 rounded object-cover" />
            )}
            {imagePreview && (
              <button onClick={() => { setSelectedImage(null); setImagePreview(null); }} className="text-[10px] text-zinc-600 hover:text-red-400">Remove</button>
            )}
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleUpload}
              disabled={uploading || !title.trim()}
              className="flex items-center gap-1.5 bg-amber-500 hover:bg-amber-400 text-zinc-900 px-4 py-1.5 rounded-lg text-xs font-medium transition-colors disabled:opacity-50"
              data-testid={`upload-confirm-${slug}`}
            >
              <Upload className="w-3 h-3" />
              {uploading ? 'Uploading...' : 'Upload'}
            </button>
            <button onClick={cancel} className="text-xs text-zinc-500 hover:text-zinc-300 px-3 py-1.5 transition-colors">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminDashboardPage({ autoLogin }) {
  const [admin, setAdmin] = useState(() => {
    const saved = localStorage.getItem('admin_user');
    const token = localStorage.getItem('admin_token');
    if (saved && token) return JSON.parse(saved);
    return null;
  });
  const [checking, setChecking] = useState(true);
  const [categories, setCategories] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [audioFiles, setAudioFiles] = useState([]);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('categories');
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [leads, setLeads] = useState([]);

  useEffect(() => {
    const token = getToken();
    if (autoLogin) {
      // Extract key from URL
      const pathParts = window.location.pathname.split('/');
      const key = pathParts[pathParts.length - 1];
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API_URL}/api/admin/magic-access`);
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.onload = function () {
        if (xhr.status === 200) {
          try {
            const data = JSON.parse(xhr.responseText);
            localStorage.setItem('admin_token', data.token);
            localStorage.setItem('admin_user', JSON.stringify(data));
            setAdmin(data);
          } catch {}
        }
        setChecking(false);
      };
      xhr.onerror = () => setChecking(false);
      xhr.send(JSON.stringify({ key }));
      return;
    }
    if (!token) { setChecking(false); return; }
    fetch(`${API_URL}/api/admin/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(data => setAdmin(data))
      .catch(() => { localStorage.removeItem('admin_token'); localStorage.removeItem('admin_user'); setAdmin(null); })
      .finally(() => setChecking(false));
  }, [autoLogin]);

  useEffect(() => {
    if (!admin) return;
    loadData();
  }, [admin]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [catRes, trackRes, audioRes, settingsRes, togglesRes] = await Promise.all([
        authFetch(`${API_URL}/api/admin/categories`).then(r => r.json()),
        authFetch(`${API_URL}/api/admin/tracks`).then(r => r.json()),
        authFetch(`${API_URL}/api/admin/audio-tracks`).then(r => r.json()),
        fetch(`${API_URL}/api/admin/settings`).then(r => r.json()),
        authFetch(`${API_URL}/api/admin/feature-toggles`).then(r => r.json()),
      ]);
      setCategories(catRes);
      setTracks(trackRes);
      setAudioFiles(audioRes);
      setSettings(settingsRes);
      setFeatureToggles(prev => ({ ...prev, ...togglesRes }));
    } catch {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const toggleCategory = async (slug, currentActive) => {
    const newActive = !currentActive;
    try {
      await authFetch(`${API_URL}/api/admin/categories/${slug}`, {
        method: 'PUT',
        body: JSON.stringify({ active: newActive }),
      });
      setCategories(prev => prev.map(c => c.slug === slug ? { ...c, active: newActive } : c));
      toast.success(`${slug} ${newActive ? 'activated' : 'deactivated'}`);
    } catch {
      toast.error('Failed to update');
    }
  };

  const setCategoryTrack = async (slug, audioUrl) => {
    try {
      await authFetch(`${API_URL}/api/admin/categories/${slug}`, {
        method: 'PUT',
        body: JSON.stringify({ featured_track: audioUrl }),
      });
      setCategories(prev => prev.map(c => c.slug === slug ? { ...c, featured_track: audioUrl } : c));
      toast.success('Track updated');
    } catch {
      toast.error('Failed to update');
    }
  };

  const updateWealthTrack = async (file) => {
    try {
      await authFetch(`${API_URL}/api/admin/settings`, {
        method: 'PUT',
        body: JSON.stringify({ wealth_activation_track: file }),
      });
      setSettings(prev => ({ ...prev, wealth_activation_track: file }));
      toast.success('Wealth Activation track updated');
    } catch {
      toast.error('Failed to update');
    }
  };

  const deleteTrack = async (trackId, trackTitle) => {
    if (!window.confirm(`Delete "${trackTitle}"?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/tracks/${trackId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error();
      setTracks(prev => prev.filter(t => t.id !== trackId));
      toast.success(`Deleted: ${trackTitle}`);
    } catch {
      toast.error('Failed to delete');
    }
  };

  const uploadTrackImage = async (trackId, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${API_URL}/api/admin/upload-image`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` },
        body: formData,
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      await fetch(`${API_URL}/api/admin/tracks/${trackId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ image_url: data.file }),
      });
      setTracks(prev => prev.map(t => t.id === trackId ? { ...t, image_url: data.file } : t));
      toast.success('Image updated');
    } catch {
      toast.error('Failed to upload image');
    }
  };

  const renameTrack = async (trackId, newTitle) => {
    if (!newTitle.trim()) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/tracks/${trackId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ title: newTitle.trim() }),
      });
      if (!res.ok) throw new Error();
      setTracks(prev => prev.map(t => t.id === trackId ? { ...t, title: newTitle.trim() } : t));
      toast.success('Title updated');
    } catch {
      toast.error('Failed to rename');
    }
  };

  const toggleTrackActive = async (trackId) => {
    try {
      const res = await authFetch(`${API_URL}/api/admin/tracks/${trackId}/toggle-active`, {
        method: 'PATCH',
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setTracks(prev => prev.map(t => t.id === trackId ? { ...t, is_active: data.is_active } : t));
      toast.success(data.is_active ? 'Track turned on' : 'Track turned off (hidden)');
    } catch {
      toast.error('Failed to update track');
    }
  };

  const reuploadTrackAudio = async (trackId, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${API_URL}/api/admin/tracks/${trackId}/reupload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` },
        body: formData,
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setTracks(prev => prev.map(t => t.id === trackId ? { ...t, audio_url: data.audio_url } : t));
      toast.success('Audio replaced');
    } catch {
      toast.error('Failed to replace audio');
    }
  };

  const uploadCategoryImage = async (slug, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${API_URL}/api/admin/upload-image`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` },
        body: formData,
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      await fetch(`${API_URL}/api/admin/categories/${slug}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ image_url: data.file }),
      });
      setCategories(prev => prev.map(c => c.slug === slug ? { ...c, image_url: data.file } : c));
      toast.success('Category image updated');
    } catch {
      toast.error('Failed to update image');
    }
  };

  const [showAddCategory, setShowAddCategory] = useState(false);
  const [newCatName, setNewCatName] = useState('');
  const [featureToggles, setFeatureToggles] = useState({
    registration: true, guest_mode: false, playback_speed: true,
    loop_repeat: true, favorites: true, listening_stats: true,
  });

  const addCategory = async () => {
    if (!newCatName.trim()) return;
    try {
      const res = await authFetch(`${API_URL}/api/admin/categories`, {
        method: 'POST',
        body: JSON.stringify({ name: newCatName.trim() }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Failed');
      }
      const cat = await res.json();
      setCategories(prev => [...prev, cat]);
      setNewCatName('');
      setShowAddCategory(false);
      toast.success(`Category "${cat.name}" created`);
    } catch (e) {
      toast.error(e.message || 'Failed to create category');
    }
  };

  const updateFeatureToggle = async (key, value) => {
    const updated = { ...featureToggles, [key]: value };
    setFeatureToggles(updated);
    try {
      await authFetch(`${API_URL}/api/admin/feature-toggles`, {
        method: 'PUT',
        body: JSON.stringify({ [key]: value }),
      });
      toast.success(`${key.replace('_', ' ')} ${value ? 'enabled' : 'disabled'}`);
    } catch {
      setFeatureToggles(prev => ({ ...prev, [key]: !value }));
      toast.error('Failed to update');
    }
  };

  const loadAnalytics = async () => {
    setAnalyticsLoading(true);
    try {
      const [aRes, lRes] = await Promise.all([
        authFetch(`${API_URL}/api/admin/analytics`),
        authFetch(`${API_URL}/api/admin/leads`),
      ]);
      const data = await aRes.json();
      const leadsData = await lRes.json().catch(() => []);
      setAnalytics(data);
      setLeads(Array.isArray(leadsData) ? leadsData : []);
    } catch {
      toast.error('Failed to load analytics');
    } finally {
      setAnalyticsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'analytics' && !analytics) loadAnalytics();
  }, [activeTab]);

  const deleteCategory = async (slug, name) => {
    if (!window.confirm(`Delete category "${name}"? Tracks in this category will NOT be deleted.`)) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/categories/${slug}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error();
      setCategories(prev => prev.filter(c => c.slug !== slug));
      toast.success(`Deleted: ${name}`);
    } catch {
      toast.error('Failed to delete');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    setAdmin(null);
  };

  if (checking) {
    return <WaveLoader />;
  }

  if (!admin) {
    return <AdminLoginPage onLogin={(data) => setAdmin(data)} />;
  }

  const categoryTracks = (slug) => tracks.filter(t => {
    const cats = t.categories || [t.category];
    return cats.includes(slug);
  });

  const toggleTrackCategory = async (trackId, slug, currentCats) => {
    const cats = currentCats.includes(slug)
      ? currentCats.filter(c => c !== slug)
      : [...currentCats, slug];
    if (cats.length === 0) {
      toast.error('Track must belong to at least one category');
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/admin/tracks/${trackId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ categories: cats, category: cats[0] }),
      });
      if (!res.ok) throw new Error();
      setTracks(prev => prev.map(t => t.id === trackId ? { ...t, categories: cats, category: cats[0] } : t));
      toast.success('Categories updated');
    } catch {
      toast.error('Failed to update');
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-zinc-900/95 backdrop-blur-md border-b border-white/10">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => document.querySelector('#root').scrollTo(0, 0)} className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors">
              <ChevronLeft className="w-4 h-4" />
              <span className="text-sm">Categories</span>
            </button>
          </div>
          <h1 className="text-lg font-serif text-amber-400 flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Admin Panel
          </h1>
          <div className="flex items-center gap-3">
            <Link to="/subtitle-editor" className="text-xs text-zinc-500 hover:text-amber-400 flex items-center gap-1 transition-colors">
              <FileText className="w-3 h-3" /> Subtitle Editor
            </Link>
            <span className="text-xs text-zinc-600">|</span>
            <span className="text-xs text-zinc-500">{admin?.email}</span>
            <button onClick={handleLogout} className="text-xs text-zinc-600 hover:text-red-400 transition-colors flex items-center gap-1" data-testid="admin-logout">
              <LogOut className="w-3 h-3" /> Logout
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-white/10 bg-zinc-900/50">
        <div className="container mx-auto px-4 max-w-4xl flex gap-1">
          {[
            { id: 'categories', label: 'Categories & Tracks', icon: Music },
            { id: 'challenges', label: 'Challenges', icon: Flame },
            { id: 'analytics', label: 'Analytics', icon: BarChart3 },
          ].map(({ id, label, icon: Icon }) => (
            <button key={id} onClick={() => setActiveTab(id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === id ? 'border-amber-500 text-amber-400' : 'border-transparent text-zinc-500 hover:text-zinc-300'
              }`} data-testid={`tab-${id}`}>
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <VoiceWave size="sm" speed={1.3} lineCount={4} />
        </div>
      ) : (
        <div className="container mx-auto px-4 py-8 max-w-4xl">

        {activeTab === 'challenges' ? (
          /* =================== CHALLENGES TAB =================== */
          <ChallengeAdminPanel authFetch={authFetch} allTracks={tracks} />
        ) : activeTab === 'analytics' ? (
          /* =================== ANALYTICS TAB =================== */
          analyticsLoading ? (
            <div className="flex items-center justify-center py-20">
              <VoiceWave size="sm" speed={1.3} lineCount={4} />
            </div>
          ) : analytics ? (
            <div className="space-y-6">
              {/* Overview Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-zinc-900/60 border border-violet-500/20 rounded-xl p-4">
                  <Headphones className="w-5 h-5 text-violet-400 mb-2" />
                  <p className="text-2xl font-serif text-white">{analytics.total_minutes}</p>
                  <p className="text-[11px] text-zinc-500">Total minutes listened</p>
                </div>
                <div className="bg-zinc-900/60 border border-blue-500/20 rounded-xl p-4">
                  <Users className="w-5 h-5 text-blue-400 mb-2" />
                  <p className="text-2xl font-serif text-white">{analytics.dau || 0}</p>
                  <p className="text-[11px] text-zinc-500">Active today (DAU)</p>
                </div>
                <div className="bg-zinc-900/60 border border-green-500/20 rounded-xl p-4">
                  <Users className="w-5 h-5 text-green-400 mb-2" />
                  <p className="text-2xl font-serif text-white">{analytics.registered_users?.length || 0}</p>
                  <p className="text-[11px] text-zinc-500">Registered users</p>
                </div>
                <div className="bg-zinc-900/60 border border-amber-500/20 rounded-xl p-4">
                  <p className="text-2xl font-serif text-white">{analytics.returning_pct || 0}%</p>
                  <p className="text-[11px] text-zinc-500">Returning users</p>
                </div>
              </div>

              <div className="flex justify-end">
                <button onClick={loadAnalytics} className="text-xs text-zinc-500 hover:text-amber-400 flex items-center gap-1 transition-colors">
                  <RefreshCw className="w-3 h-3" /> Refresh
                </button>
              </div>

              {/* Audio Analytics */}
              <div className="bg-zinc-900/60 border border-white/5 rounded-xl p-5" data-testid="audio-analytics">
                <div className="flex items-center gap-2 mb-4">
                  <Music className="w-5 h-5 text-violet-400" />
                  <h3 className="text-base font-medium text-violet-400">Audio Analytics</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="text-[10px] text-zinc-600 uppercase tracking-wider border-b border-zinc-800">
                        <th className="py-2 pr-2">#</th>
                        <th className="py-2 pr-2">Track</th>
                        <th className="py-2 pr-2 text-right">Plays</th>
                        <th className="py-2 pr-2 text-right">Minutes</th>
                        <th className="py-2 text-right">Favs</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analytics.audio_analytics?.map((t, i) => (
                        <tr key={t.track_id} className="border-b border-zinc-800/50 text-sm">
                          <td className="py-2 pr-2 text-zinc-600 font-mono text-xs">{i + 1}</td>
                          <td className="py-2 pr-2 text-zinc-300 truncate max-w-[200px]">{t.title}</td>
                          <td className="py-2 pr-2 text-right text-amber-400 font-mono text-xs">{t.play_count}</td>
                          <td className="py-2 pr-2 text-right text-violet-400 font-mono text-xs">{t.total_minutes}</td>
                          <td className="py-2 text-right text-red-400 font-mono text-xs">{t.favorites}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Category Analytics */}
              <div className="bg-zinc-900/60 border border-white/5 rounded-xl p-5" data-testid="category-analytics">
                <div className="flex items-center gap-2 mb-4">
                  <BarChart3 className="w-5 h-5 text-teal-400" />
                  <h3 className="text-base font-medium text-teal-400">Category Analytics</h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {analytics.category_analytics?.map(c => (
                    <div key={c.slug} className="bg-zinc-800/30 rounded-lg p-3">
                      <p className="text-sm text-zinc-300 font-medium">{c.name}</p>
                      <div className="flex gap-4 mt-1">
                        <span className="text-[10px] text-amber-400">{c.total_plays} plays</span>
                        <span className="text-[10px] text-violet-400">{c.total_minutes} min total</span>
                        <span className="text-[10px] text-zinc-500">{c.avg_minutes} min avg</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* User Analytics */}
              <div className="bg-zinc-900/60 border border-white/5 rounded-xl p-5" data-testid="user-analytics">
                <div className="flex items-center gap-2 mb-4">
                  <Users className="w-5 h-5 text-green-400" />
                  <h3 className="text-base font-medium text-green-400">User Analytics</h3>
                </div>
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div className="text-center">
                    <p className="text-xl font-serif text-white">{analytics.dau || 0}</p>
                    <p className="text-[10px] text-zinc-500">DAU</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xl font-serif text-white">{analytics.returning_pct || 0}%</p>
                    <p className="text-[10px] text-zinc-500">Returning</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xl font-serif text-white">{analytics.total_listeners || 0}</p>
                    <p className="text-[10px] text-zinc-500">Total listeners</p>
                  </div>
                </div>
                {analytics.reg_by_day?.length > 0 && (
                  <>
                    <p className="text-[11px] text-zinc-500 mb-2">New registrations (recent)</p>
                    <div className="flex gap-1 items-end h-16">
                      {analytics.reg_by_day.slice(0, 14).reverse().map(d => {
                        const maxCount = Math.max(...analytics.reg_by_day.map(x => x.count), 1);
                        const barH = Math.max((d.count / maxCount) * 48, 2);
                        return (
                          <div key={d.date} className="flex-1 flex flex-col items-center" title={`${d.date}: ${d.count}`}>
                            <div className="w-full bg-green-500/40 rounded-t" style={{ height: `${barH}px` }} />
                            <span className="text-[8px] text-zinc-700 mt-0.5 truncate w-full text-center">{d.date.slice(5)}</span>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>

              {/* Guest Conversion */}
              <div className="bg-zinc-900/60 border border-white/5 rounded-xl p-5" data-testid="guest-conversion">
                <div className="flex items-center gap-2 mb-4">
                  <Users className="w-5 h-5 text-amber-400" />
                  <h3 className="text-base font-medium text-amber-400">Guest Conversion</h3>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <p className="text-xl font-serif text-white">{analytics.guest_plays || 0}</p>
                    <p className="text-[10px] text-zinc-500">Guest plays</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xl font-serif text-white">{analytics.guest_users?.length || 0}</p>
                    <p className="text-[10px] text-zinc-500">Guest users</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xl font-serif text-white">{analytics.guest_conversions || 0}</p>
                    <p className="text-[10px] text-zinc-500">Converted to registered</p>
                  </div>
                </div>
              </div>

              {/* DNU - Daily New Users (Anonymous) */}
              <div className="bg-zinc-900/60 border border-white/5 rounded-xl p-5" data-testid="dnu-stats">
                <div className="flex items-center gap-2 mb-4">
                  <Users className="w-5 h-5 text-cyan-400" />
                  <h3 className="text-base font-medium text-cyan-400">DNU — Daily New Visitors (unregistered)</h3>
                </div>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="text-center">
                    <p className="text-xl font-serif text-white">{analytics.anonymous_users?.total_count || 0}</p>
                    <p className="text-[10px] text-zinc-500">Total anonymous visitors</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xl font-serif text-white">{analytics.anonymous_users?.total_minutes || 0}</p>
                    <p className="text-[10px] text-zinc-500">Total minutes (anonymous)</p>
                  </div>
                </div>
                {analytics.dnu?.length > 0 && (
                  <>
                    <p className="text-[11px] text-zinc-500 mb-2">Unique visitors per day</p>
                    <div className="flex gap-1 items-end h-16 mb-4">
                      {analytics.dnu.slice(0, 14).reverse().map(d => {
                        const maxCount = Math.max(...analytics.dnu.map(x => x.count), 1);
                        const barH = Math.max((d.count / maxCount) * 48, 2);
                        return (
                          <div key={d.date} className="flex-1 flex flex-col items-center" title={`${d.date}: ${d.count} visitors`}>
                            <div className="w-full bg-cyan-500/40 rounded-t" style={{ height: `${barH}px` }} />
                            <span className="text-[8px] text-zinc-700 mt-0.5 truncate w-full text-center">{d.date.slice(5)}</span>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
                {analytics.anonymous_users?.top_users?.length > 0 && (
                  <>
                    <p className="text-[11px] text-zinc-500 mb-2">Top anonymous users by time spent</p>
                    <div className="space-y-1">
                      {analytics.anonymous_users.top_users.map((u, i) => (
                        <div key={i} className="flex items-center justify-between py-2 px-3 rounded-lg bg-zinc-800/30">
                          <div>
                            <p className="text-xs text-zinc-400 font-mono">{u.anon_id}</p>
                            <p className="text-[10px] text-zinc-600">First: {u.first_seen ? new Date(u.first_seen).toLocaleDateString() : '—'}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-cyan-400 font-mono">{u.minutes} min</p>
                            <p className="text-[10px] text-zinc-600">Last: {u.last_seen ? new Date(u.last_seen).toLocaleDateString() : '—'}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>

              {/* Email-Only Leads */}
              <div className="bg-zinc-900/60 border border-amber-500/15 rounded-xl p-5" data-testid="leads-section">
                <div className="flex items-center gap-2 mb-4">
                  <Sparkles className="w-5 h-5 text-amber-400" />
                  <h3 className="text-base font-medium text-amber-400">
                    Leads / Email-Only Users ({leads.length})
                  </h3>
                </div>
                <p className="text-[11px] text-zinc-500 mb-3">
                  Users who entered just an email on a category landing page (e.g. /confidence). Lightweight accounts scoped to one category.
                </p>
                {leads.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-left">
                      <thead>
                        <tr className="text-[10px] text-zinc-600 uppercase tracking-wider border-b border-zinc-800">
                          <th className="py-2 pr-2">Email</th>
                          <th className="py-2 pr-2">Category</th>
                          <th className="py-2 pr-2 text-right">Visits</th>
                          <th className="py-2 pr-2 text-right">Min</th>
                          <th className="py-2 pr-2">First seen</th>
                          <th className="py-2">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {leads.map((l) => (
                          <tr key={l.email} className="border-b border-zinc-800/50 text-sm" data-testid={`lead-row-${l.email}`}>
                            <td className="py-2 pr-2 text-zinc-300 truncate max-w-[200px]">{l.email}</td>
                            <td className="py-2 pr-2 text-amber-400 capitalize text-xs">{l.category}</td>
                            <td className="py-2 pr-2 text-right text-zinc-400 font-mono text-xs">{l.visit_count}</td>
                            <td className="py-2 pr-2 text-right text-violet-400 font-mono text-xs">{l.total_minutes}</td>
                            <td className="py-2 pr-2 text-zinc-600 text-[10px]">
                              {l.first_seen ? new Date(l.first_seen).toLocaleDateString() : '—'}
                            </td>
                            <td className="py-2">
                              {l.converted ? (
                                <span className="text-[10px] text-green-400">Converted</span>
                              ) : (
                                <span className="text-[10px] text-zinc-500">Lead</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-xs text-zinc-600">No email leads yet. Share <span className="text-amber-400">/confidence</span> to start collecting.</p>
                )}
              </div>

              {/* Registered Users List */}
              <div className="bg-zinc-900/60 border border-white/5 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-4">
                  <Users className="w-5 h-5 text-green-400" />
                  <h3 className="text-base font-medium text-green-400">Registered Users ({analytics.registered_users?.length || 0})</h3>
                </div>
                {analytics.registered_users?.length > 0 ? (
                  <div className="space-y-1">
                    {analytics.registered_users.map(u => (
                      <div key={u.user_id} className="flex items-center justify-between py-2 px-3 rounded-lg bg-zinc-800/30">
                        <div>
                          <p className="text-sm text-zinc-300">{u.name || 'No name'}</p>
                          <p className="text-[10px] text-zinc-600">{u.email}</p>
                        </div>
                        <p className="text-[10px] text-zinc-700">{u.created_at ? new Date(u.created_at).toLocaleDateString() : ''}</p>
                      </div>
                    ))}
                  </div>
                ) : <p className="text-xs text-zinc-600">No registered users yet</p>}
              </div>

              {/* Recent Activity */}
              <div className="bg-zinc-900/60 border border-white/5 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-4">
                  <Clock className="w-5 h-5 text-zinc-400" />
                  <h3 className="text-base font-medium text-zinc-400">Recent Activity</h3>
                </div>
                {analytics.recent_activity?.length > 0 ? (
                  <div className="space-y-1">
                    {analytics.recent_activity.map((a, i) => (
                      <div key={i} className="flex items-center justify-between py-2 px-3 rounded-lg bg-zinc-800/30">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-zinc-300 truncate">{a.user}</p>
                          <p className="text-[10px] text-zinc-600 truncate">Played: {a.track}</p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-[10px] text-amber-400">{a.total_minutes} min</p>
                          <p className="text-[10px] text-zinc-700">{a.at ? new Date(a.at).toLocaleString() : ''}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : <p className="text-xs text-zinc-600">No recent activity</p>}
              </div>
            </div>
          ) : <p className="text-center text-zinc-600 py-10">Failed to load analytics</p>
        ) : (
          /* =================== CATEGORIES TAB =================== */
          <>

          {/* Wealth Activation Selector */}
          <div className="bg-zinc-900/60 border border-amber-500/20 rounded-xl p-5 mb-8" data-testid="wealth-settings">
            <div className="flex items-center gap-2 mb-3">
              <Zap className="w-5 h-5 text-amber-400" />
              <h2 className="text-base font-medium text-amber-400">Wealth Activation (Dashboard Player)</h2>
            </div>
            <p className="text-xs text-zinc-500 mb-3">Choose which audio track plays in the Wealth Activation player on the user Dashboard.</p>
            <select
              value={settings.wealth_activation_track || '/audio/wealth_activation.mp3'}
              onChange={(e) => updateWealthTrack(e.target.value)}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white focus:border-amber-500 focus:outline-none"
              data-testid="wealth-track-select"
            >
              {audioFiles.map(t => (
                <option key={t.file} value={t.file}>{t.name}</option>
              ))}
            </select>
          </div>

          {/* Feature Toggles */}
          <div className="bg-zinc-900/60 border border-violet-500/20 rounded-xl p-5 mb-8" data-testid="feature-toggles">
            <div className="flex items-center gap-2 mb-4">
              <Settings className="w-5 h-5 text-violet-400" />
              <h2 className="text-base font-medium text-violet-400">Feature Controls</h2>
            </div>
            <p className="text-xs text-zinc-500 mb-4">Toggle features on/off for users. Changes take effect immediately.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { key: 'registration', label: 'Registration & Login', desc: 'User signup and login system' },
                { key: 'guest_mode', label: 'Guest Mode', desc: 'Allow limited listening without signup' },
                { key: 'playback_speed', label: 'Playback Speed', desc: 'Speed control (0.75x - 1.5x)' },
                { key: 'loop_repeat', label: 'Loop / Repeat', desc: 'Loop and repeat mode on player' },
                { key: 'favorites', label: 'Favorites', desc: 'Save tracks to My Favorites' },
                { key: 'listening_stats', label: 'Listening Stats', desc: 'Track minutes listened & history' },
              ].map(({ key, label, desc }) => (
                <div key={key} className="flex items-center justify-between bg-zinc-800/50 rounded-lg px-4 py-3">
                  <div>
                    <p className="text-sm text-zinc-300">{label}</p>
                    <p className="text-[10px] text-zinc-600">{desc}</p>
                  </div>
                  <button onClick={() => updateFeatureToggle(key, !featureToggles[key])}
                    data-testid={`toggle-feature-${key}`}>
                    {featureToggles[key] ? (
                      <ToggleRight className="w-8 h-8 text-green-400" />
                    ) : (
                      <ToggleLeft className="w-8 h-8 text-zinc-600" />
                    )}
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Categories */}
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-serif text-white">Categories</h2>
            {!showAddCategory ? (
              <button onClick={() => setShowAddCategory(true)}
                className="flex items-center gap-1.5 text-xs text-zinc-500 hover:text-amber-400 transition-colors"
                data-testid="add-category-btn">
                <Plus className="w-3.5 h-3.5" /> Add Category
              </button>
            ) : (
              <div className="flex items-center gap-2">
                <input type="text" value={newCatName} onChange={(e) => setNewCatName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') addCategory(); if (e.key === 'Escape') { setShowAddCategory(false); setNewCatName(''); } }}
                  placeholder="Category name"
                  className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-1.5 text-sm text-white focus:border-amber-500 focus:outline-none w-48"
                  autoFocus data-testid="new-category-input" />
                <button onClick={addCategory} disabled={!newCatName.trim()}
                  className="bg-amber-500 hover:bg-amber-400 text-zinc-900 px-3 py-1.5 rounded-lg text-xs font-medium disabled:opacity-50"
                  data-testid="confirm-add-category">Create</button>
                <button onClick={() => { setShowAddCategory(false); setNewCatName(''); }}
                  className="text-xs text-zinc-600 hover:text-zinc-400">Cancel</button>
              </div>
            )}
          </div>
          <div className="space-y-4">
            {categories.map((cat) => {
              const catTracks = categoryTracks(cat.slug);
              return (
                <div
                  key={cat.slug}
                  className={`rounded-xl border p-5 transition-all ${
                    cat.active ? 'bg-zinc-900/60 border-green-500/30' : 'bg-zinc-900/30 border-zinc-800'
                  }`}
                  data-testid={`admin-category-${cat.slug}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <label
                        className="relative cursor-pointer flex-shrink-0 group/img"
                        title="Click or drop image to change"
                        onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('ring-2', 'ring-amber-500'); }}
                        onDragLeave={(e) => { e.preventDefault(); e.currentTarget.classList.remove('ring-2', 'ring-amber-500'); }}
                        onDrop={(e) => { e.preventDefault(); e.currentTarget.classList.remove('ring-2', 'ring-amber-500'); const f = e.dataTransfer.files?.[0]; if (f && f.type.startsWith('image/')) uploadCategoryImage(cat.slug, f); }}
                      >
                        <input type="file" accept="image/*" className="hidden" onChange={(e) => { if (e.target.files?.[0]) uploadCategoryImage(cat.slug, e.target.files[0]); e.target.value=''; }} />
                        <img src={cat.image_url} alt={cat.name} className="w-12 h-12 rounded-lg object-cover" />
                        <div className="absolute inset-0 rounded-lg bg-black/50 opacity-0 group-hover/img:opacity-100 flex items-center justify-center transition-opacity">
                          <ImageIcon className="w-4 h-4 text-white" />
                        </div>
                      </label>
                      <div>
                        <h3 className="text-sm font-medium text-white">{cat.name}</h3>
                        <p className="text-xs text-zinc-500">{cat.slug} &middot; {catTracks.length} tracks</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => toggleCategory(cat.slug, cat.active)}
                        className="flex items-center gap-2"
                        data-testid={`toggle-${cat.slug}`}
                      >
                        {cat.active ? (
                          <>
                            <span className="text-xs text-green-400 font-medium">Active</span>
                            <ToggleRight className="w-8 h-8 text-green-400" />
                          </>
                        ) : (
                          <>
                            <span className="text-xs text-zinc-600">Inactive</span>
                            <ToggleLeft className="w-8 h-8 text-zinc-600" />
                          </>
                        )}
                      </button>
                      <button onClick={() => deleteCategory(cat.slug, cat.name)}
                        className="p-1 text-zinc-700 hover:text-red-400 transition-colors" title="Delete category">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Featured Track selector */}
                  <div className="mt-3 pt-3 border-t border-white/5">
                    <label className="text-[11px] text-zinc-500 mb-1.5 block">Featured Track</label>
                    <select
                      value={cat.featured_track || ''}
                      onChange={(e) => setCategoryTrack(cat.slug, e.target.value)}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white focus:border-amber-500 focus:outline-none"
                      data-testid={`featured-track-${cat.slug}`}
                    >
                      <option value="">None selected</option>
                      {audioFiles.map(t => (
                        <option key={t.file} value={t.file}>{t.name}</option>
                      ))}
                    </select>
                  </div>

                  {/* Existing tracks in this category */}
                  {catTracks.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-white/5">
                      <p className="text-[11px] text-zinc-500 mb-2">Tracks in category:</p>
                      <div className="space-y-2">
                        {catTracks.map(track => {
                          const trackCats = track.categories || [track.category];
                          return (
                            <div key={track.id} className={`bg-zinc-800/50 rounded-lg p-3 group transition-opacity ${track.is_active === false ? 'opacity-40' : ''}`} data-testid={`admin-track-row-${track.id}`}>
                              <div className="flex items-center gap-3">
                                {/* Track image - click or drag & drop */}
                                <label
                                  className="relative cursor-pointer flex-shrink-0"
                                  title="Click or drop image"
                                  onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('ring-2', 'ring-amber-500'); }}
                                  onDragLeave={(e) => { e.preventDefault(); e.currentTarget.classList.remove('ring-2', 'ring-amber-500'); }}
                                  onDrop={(e) => { e.preventDefault(); e.currentTarget.classList.remove('ring-2', 'ring-amber-500'); const f = e.dataTransfer.files?.[0]; if (f && f.type.startsWith('image/')) uploadTrackImage(track.id, f); }}
                                >
                                  <input type="file" accept="image/*" className="hidden" onChange={(e) => { if (e.target.files?.[0]) uploadTrackImage(track.id, e.target.files[0]); }} />
                                  {track.image_url ? (
                                    <img src={track.image_url} alt="" className="w-10 h-10 rounded-lg object-cover" />
                                  ) : (
                                    <div className="w-10 h-10 rounded-lg bg-zinc-700/50 flex items-center justify-center hover:bg-zinc-700 transition-colors">
                                      <ImageIcon className="w-4 h-4 text-zinc-500" />
                                    </div>
                                  )}
                                  <div className="absolute inset-0 rounded-lg bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                                    <ImageIcon className="w-3 h-3 text-white" />
                                  </div>
                                </label>
                                <TrackTitle trackId={track.id} title={track.title} onRename={renameTrack} />
                                <div className="flex items-center gap-1">
                                  <span className="text-[10px] text-zinc-600">{Math.floor((track.duration || 0) / 60)}:{((track.duration || 0) % 60).toString().padStart(2, '0')}</span>
                                  {/* On/Off toggle */}
                                  <button
                                    onClick={() => toggleTrackActive(track.id)}
                                    className="p-1 transition-colors"
                                    title={track.is_active === false ? 'Turn track on (show in library)' : 'Turn track off (hide from library)'}
                                    data-testid={`toggle-track-${track.id}`}
                                  >
                                    {track.is_active === false ? (
                                      <ToggleLeft className="w-5 h-5 text-zinc-600 hover:text-zinc-400" />
                                    ) : (
                                      <ToggleRight className="w-5 h-5 text-emerald-400 hover:text-emerald-300" />
                                    )}
                                  </button>
                                  {/* Replace audio button */}
                                  <label className="opacity-0 group-hover:opacity-100 p-1.5 text-zinc-600 hover:text-amber-400 transition-all cursor-pointer" title="Replace audio file">
                                    <input type="file" accept=".mp3,.mov,.mp4,.wav,.m4a,audio/*,video/*" className="hidden"
                                      onChange={(e) => { if (e.target.files?.[0]) reuploadTrackAudio(track.id, e.target.files[0]); e.target.value=''; }} />
                                    <RefreshCw className="w-3 h-3" />
                                  </label>
                                  <button
                                    onClick={() => deleteTrack(track.id, track.title)}
                                    className="opacity-0 group-hover:opacity-100 text-zinc-600 hover:text-red-400 transition-all p-0.5"
                                    title="Delete track"
                                    data-testid={`delete-track-${track.id}`}
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                </div>
                              </div>
                              {/* Multi-category tags */}
                              <div className="flex flex-wrap gap-1.5 mt-2">
                                {categories.map(c => {
                                  const isIn = trackCats.includes(c.slug);
                                  return (
                                    <button
                                      key={c.slug}
                                      onClick={() => toggleTrackCategory(track.id, c.slug, trackCats)}
                                      className={`text-[10px] px-2 py-0.5 rounded-full border transition-all ${
                                        isIn
                                          ? 'bg-amber-500/20 border-amber-500/40 text-amber-300'
                                          : 'bg-zinc-800 border-zinc-700 text-zinc-600 hover:border-zinc-500 hover:text-zinc-400'
                                      }`}
                                      data-testid={`tag-${track.id}-${c.slug}`}
                                    >
                                      {c.name}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Upload new track */}
                  <CategoryUploader
                    slug={cat.slug}
                    categoryName={cat.name}
                    onUploaded={loadData}
                  />
                </div>
              );
            })}
          </div>
          </>
        )}
        </div>
      )}
    </div>
  );
}
